import {useApolloClient, useMutation} from "@apollo/client";
import {
    CancelTerminalReleaseDocument,
    CancelTerminalReleaseResult,
    CancelTerminalReleaseVars,
    Cargo,
    Contract,
    CreateTerminalReleaseDocument,
    CreateTerminalReleaseResult,
    CreateTerminalReleaseVars,
    GetCargoListDocument, getCargoListPathBuilder,
    GetCargoListQueryResult,
    GetCargoListQueryVars,
    GetContractListDocument,
    GetContractListQueryResult,
    GetContractListQueryVars,
    GetTerminalReleaseDocument,
    GetTerminalReleaseListDocument,
    GetTerminalReleaseListResult,
    GetTerminalReleaseListVars,
    GetTerminalReleaseResult,
    GetTerminalReleaseVars, RecoveryTerminalReleaseDocument,
    RecoveryTerminalReleaseResult,
    RecoveryTerminalReleaseVars, terminalReleaseListPathBuilder, TerminalReleaseState
} from "~/apps/packages/warehouse-closure/queries";
import {useCallback, useEffect, useMemo} from "react";
import {tuple} from "~/types";
import {useErrorHandler} from "~/components/ErrorHandling";
import {useQuery} from "~/apollo/patches";
import moment from "moment";

type useGetTerminalReleaseListVariables = {
    query?: string
    status?: TerminalReleaseState[]
}


export const useGetTerminalReleaseList = (variables?: useGetTerminalReleaseListVariables) => {
    const apollo = useApolloClient();
    const handleError = useErrorHandler();
    const result = useQuery<GetTerminalReleaseListResult, GetTerminalReleaseListVars>(GetTerminalReleaseListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            ...variables,
            pathBuilder: terminalReleaseListPathBuilder
        }
    });
    const {loading, error, refetch, data, fetchMore: _fetchMore} = result;

    const canFetchMore = Boolean(data?.terminalReleases?.pageInfo?.nextPage) && !loading;
    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.terminalReleases.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    // remove previous cache
    useEffect(() => {
        if (!data && loading) {
            apollo.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleases"});
        }
    }, [data, loading, apollo])

    return {
        data: data?.terminalReleases?.terminalReleases,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}


export const useGetTerminalRelease = (id:  string) => {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} = useQuery<GetTerminalReleaseResult, GetTerminalReleaseVars>(GetTerminalReleaseDocument, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
        variables: {
            DocumentID: id
        }
    })

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    return {
        data,
        loading: loading || error != null
    }
}


export function useContractList() {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} =  useQuery<GetContractListQueryResult, GetContractListQueryVars>(GetContractListDocument, {
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    return {
        data: data?.terminalReleaseContracts?.contracts,
        loading: loading || error != null
    }
}


type useCargoListOptions = {
    type?: Cargo["type"]
    query?: string
    excludeCodes?: string[]
}
export function useCargoList(options?: useCargoListOptions) {
    const handleError = useErrorHandler();
    const {loading, error, refetch, data, fetchMore: _fetchMore} = useQuery<GetCargoListQueryResult, GetCargoListQueryVars>(GetCargoListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: options.query,
            type: options.type || "Контейнер",
            excludeCodes: options.excludeCodes,
            pathBuilder: getCargoListPathBuilder,
        }
    });

    const canFetchMore = Boolean(data?.terminalReleaseCargos?.pageInfo?.nextPage) && !loading;
    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.terminalReleaseCargos.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    return {
        data: data?.terminalReleaseCargos?.cargos,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}


export function useCreateTerminalRelease() {
    const client = useApolloClient();
    const [mutate, {loading, error, called, data}] = useMutation<CreateTerminalReleaseResult, CreateTerminalReleaseVars>(CreateTerminalReleaseDocument, {
        notifyOnNetworkStatusChange: true,
        errorPolicy: "all",
        onCompleted: () => {
            client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleases"});
            client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleaseCargos"});
            client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleaseContracts"});
        }
    });

    type Input = {
        contract_number: Contract["number"]
        date: Date
        cargos: {
            number: string
        }[]
    }

    const create = useCallback((input: Input) => {
        return mutate({
            variables: {
                input: {
                    terminal_release: input.cargos.map(cargo => ({
                        contract_number: input.contract_number,
                        container: cargo.number,
                        date_of_commercial_release: moment(input.date).format("YYYY-MM-DD")
                    }))
                }
            },
            awaitRefetchQueries: true
        })
    }, [mutate]);

    // invalidate cache when unmount
    useEffect(() => () => {
        client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleaseCargos"});
        client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleaseContracts"});
    }, []);

    return tuple(create, {called, loading, error, data: data?.CreateTerminalRelease?.terminal_release});
}


export function useCancelTerminalRelease(id: string) {
    const client = useApolloClient();
    const [execute, {loading, error, called}] = useMutation<CancelTerminalReleaseResult, CancelTerminalReleaseVars>(CancelTerminalReleaseDocument, {
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
            client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleaseCargos"});
        },
        variables: {
            DocumentID: id,
            input: {},
        },
    });
    const success = called && error == null && !loading;

    const cancel = async () => {
        await execute()
    }

    return tuple(cancel, {loading, success, called, error});
}


export function useRecoveryTerminalRelease(id: string) {
    const client = useApolloClient();
    const [execute, {loading, error, called}] = useMutation<RecoveryTerminalReleaseResult, RecoveryTerminalReleaseVars>(RecoveryTerminalReleaseDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            DocumentID: id,
            input: {},
        },
        onCompleted: () => {
            client.cache.evict({id: "ROOT_QUERY", fieldName: "terminalReleaseCargos"});
        },
    });
    const success = called && error == null && !loading;

    const cancel = async () => {
        await execute()
    }

    return tuple(cancel, {loading, success, called, error});
}